import {
  Laptop,
  Monitor,
  HardDrive,
  Smartphone,
  Tablet,
  Headphones,
  SlidersHorizontal,
  Cpu,
  Server,
  Printer,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

export interface DynamicSpecField {
  key: string;
  label: string;
  type: 'select' | 'text';
  placeholder?: string;
  options?: string[];
  defaultValue?: string;
  helpText?: string;
}

export interface AssetCategoryConfig {
  id: string;
  label: string;
  defaultName: string;
  icon: LucideIcon;
  badge: string;
  fields: DynamicSpecField[];
}

export const ASSET_CATEGORY_CONFIGS: Record<string, AssetCategoryConfig> = {
  monitor: {
    id: 'monitor',
    label: 'Monitor / Display',
    defaultName: 'Monitor',
    icon: Monitor,
    badge: 'Display Specifications',
    fields: [
      {
        key: 'screenSize',
        label: 'Display Size',
        type: 'select',
        defaultValue: '27-inch',
        options: ['24-inch (FHD)', '27-inch (QHD / 4K)', '32-inch (4K UHD)', '34-inch Ultrawide (21:9)', 'Dual 24-inch Setup', 'Dual 27-inch Setup'],
      },
      {
        key: 'resolution',
        label: 'Resolution',
        type: 'select',
        defaultValue: '4K UHD (3840x2160)',
        options: ['4K UHD (3840x2160)', 'QHD 2K (2560x1440)', 'Full HD (1920x1080)', '5K Retina (5120x2880)'],
      },
      {
        key: 'refreshRate',
        label: 'Refresh Rate',
        type: 'select',
        defaultValue: '60 Hz (Productivity)',
        options: ['60 Hz (Productivity)', '75 Hz - 100 Hz', '120 Hz / 144 Hz (Smooth)'],
      },
      {
        key: 'connectivity',
        label: 'Connectivity & Hub',
        type: 'select',
        defaultValue: 'USB-C with 90W PD & Hub',
        options: [
          'USB-C with 90W PD & Hub',
          'HDMI & DisplayPort',
          'Thunderbolt 4 Daisy-Chain',
          'Dual HDMI + DP + USB-C',
        ],
      },
      {
        key: 'standType',
        label: 'Stand / Mount',
        type: 'select',
        defaultValue: 'Height-Adjustable Stand',
        options: ['Height-Adjustable Stand', 'VESA Desk Arm Mount', 'Standard Tilt Stand', 'Wall Mount Kit'],
      },
      {
        key: 'panelType',
        label: 'Panel & Color',
        type: 'select',
        defaultValue: 'IPS Panel (Wide Angle / Color Accurate)',
        options: [
          'IPS Panel (Wide Angle / Color Accurate)',
          'Curved UltraWide Panel',
          'OLED / High Contrast',
          'Standard Anti-Glare Office Panel',
        ],
      },
    ],
  },
  laptop: {
    id: 'laptop',
    label: 'Laptop / Notebook',
    defaultName: 'Laptop',
    icon: Laptop,
    badge: 'Laptop Hardware Specs',
    fields: [
      {
        key: 'processor',
        label: 'Processor / CPU',
        type: 'select',
        defaultValue: 'Apple M3 / M3 Pro',
        options: [
          'Apple M3 / M3 Pro',
          'Apple M3 Max / M4',
          'Intel Core i7 (13th/14th Gen)',
          'Intel Core i9 (High Performance)',
          'AMD Ryzen 7 / 9',
          'Intel Core i5 (Standard)',
        ],
      },
      {
        key: 'ram',
        label: 'RAM / Memory',
        type: 'select',
        defaultValue: '16 GB',
        options: ['16 GB', '32 GB (Engineering / Design)', '64 GB (Intensive Dev)', '8 GB (Light Tasks)', '128 GB (Extreme)'],
      },
      {
        key: 'storage',
        label: 'Storage Capacity',
        type: 'select',
        defaultValue: '512 GB SSD',
        options: ['512 GB SSD', '1 TB SSD', '2 TB SSD', '256 GB SSD'],
      },
      {
        key: 'os',
        label: 'Operating System',
        type: 'select',
        defaultValue: 'macOS Sonoma / Sequoia',
        options: ['macOS Sonoma / Sequoia', 'Windows 11 Pro', 'Ubuntu Linux LTS', 'Dual Boot (Win + Linux)'],
      },
      {
        key: 'screenSize',
        label: 'Screen Size',
        type: 'select',
        defaultValue: '14-inch (Portable)',
        options: ['14-inch (Portable)', '16-inch (Large Workspace)', '13.3-inch (Ultra-Light)', '15.6-inch (Standard)'],
      },
      {
        key: 'keyboardLayout',
        label: 'Keyboard & Form',
        type: 'select',
        defaultValue: 'US English Backlit',
        options: ['US English Backlit', 'UK English', 'International layout with TouchID/Fingerprint'],
      },
    ],
  },
  desktop: {
    id: 'desktop',
    label: 'Desktop / Workstation',
    defaultName: 'Desktop Workstation',
    icon: HardDrive,
    badge: 'Workstation Specs',
    fields: [
      {
        key: 'formFactor',
        label: 'Form Factor',
        type: 'select',
        defaultValue: 'Compact Mini PC / Mac Mini',
        options: [
          'Compact Mini PC / Mac Mini',
          'Mid-Tower Workstation',
          'Full-Tower Workstation',
          'All-in-One (iMac / Integrated)',
          'Mac Studio',
        ],
      },
      {
        key: 'processor',
        label: 'Processor / CPU',
        type: 'select',
        defaultValue: 'Intel Core i7 (14th Gen)',
        options: [
          'Intel Core i7 (14th Gen)',
          'Intel Core i9 (14th Gen)',
          'AMD Ryzen 9 / Threadripper',
          'Apple M2/M3 Max / Ultra',
          'Intel Core i5',
        ],
      },
      {
        key: 'ram',
        label: 'RAM / Memory',
        type: 'select',
        defaultValue: '32 GB',
        options: ['32 GB', '64 GB', '128 GB', '16 GB'],
      },
      {
        key: 'storage',
        label: 'Storage',
        type: 'select',
        defaultValue: '1 TB NVMe SSD',
        options: ['1 TB NVMe SSD', '2 TB NVMe SSD', '512 GB SSD', '1 TB SSD + 2TB Secondary HDD'],
      },
      {
        key: 'gpu',
        label: 'Dedicated GPU / Graphics',
        type: 'select',
        defaultValue: 'Integrated Graphics (Standard)',
        options: [
          'Integrated Graphics (Standard)',
          'NVIDIA RTX 4070 / 4080 (12GB+)',
          'NVIDIA RTX A4000 (CAD / Workstation)',
          'Apple Integrated 30-Core GPU',
        ],
      },
      {
        key: 'os',
        label: 'Operating System',
        type: 'select',
        defaultValue: 'Windows 11 Pro',
        options: ['Windows 11 Pro', 'Ubuntu Linux 24.04 LTS', 'macOS', 'Red Hat Enterprise Linux'],
      },
    ],
  },
  phone: {
    id: 'phone',
    label: 'Mobile Phone / Smartphone',
    defaultName: 'Mobile Phone',
    icon: Smartphone,
    badge: 'Mobile Device Details',
    fields: [
      {
        key: 'platform',
        label: 'Platform / Brand',
        type: 'select',
        defaultValue: 'Apple iPhone (iOS)',
        options: ['Apple iPhone (iOS)', 'Samsung Galaxy (Android)', 'Google Pixel (Android)'],
      },
      {
        key: 'tier',
        label: 'Model Tier',
        type: 'select',
        defaultValue: 'Standard Enterprise Tier (e.g. iPhone 15 / S24)',
        options: [
          'Standard Enterprise Tier (e.g. iPhone 15 / S24)',
          'Pro / Flagship Tier (e.g. iPhone 15/16 Pro / S24 Ultra)',
          'Base / Essential Tier (e.g. iPhone SE / Galaxy A-Series)',
        ],
      },
      {
        key: 'storage',
        label: 'Storage Capacity',
        type: 'select',
        defaultValue: '256 GB',
        options: ['128 GB', '256 GB', '512 GB', '1 TB'],
      },
      {
        key: 'simConnectivity',
        label: 'Connectivity & SIM',
        type: 'select',
        defaultValue: 'eSIM + Physical SIM (Corporate 5G)',
        options: [
          'eSIM + Physical SIM (Corporate 5G)',
          'Dual eSIM',
          'Device Only (Existing SIM)',
          'Wi-Fi Only (Testing / Staging)',
        ],
      },
      {
        key: 'accessories',
        label: 'Included Accessories',
        type: 'select',
        defaultValue: 'Protective Case + Screen Guard',
        options: [
          'Protective Case + Screen Guard',
          'Fast 30W Charging Adapter + Case',
          'Full Bundle (Case + Glass + Charger + Car Mount)',
          'Device Only',
        ],
      },
    ],
  },
  tablet: {
    id: 'tablet',
    label: 'Tablet / iPad',
    defaultName: 'Tablet',
    icon: Tablet,
    badge: 'Tablet Details',
    fields: [
      {
        key: 'modelSize',
        label: 'Model & Screen Size',
        type: 'select',
        defaultValue: 'Apple iPad Pro 11-inch',
        options: [
          'Apple iPad Pro 11-inch',
          'Apple iPad Pro 13-inch',
          'Apple iPad Air 11-inch',
          'Apple iPad 10.9-inch (Standard)',
          'Samsung Galaxy Tab S9',
          'Microsoft Surface Pro',
        ],
      },
      {
        key: 'storage',
        label: 'Storage',
        type: 'select',
        defaultValue: '256 GB',
        options: ['128 GB', '256 GB', '512 GB', '1 TB'],
      },
      {
        key: 'accessories',
        label: 'Input & Accessories',
        type: 'select',
        defaultValue: 'Stylus (Apple Pencil / S-Pen) + Keyboard Folio',
        options: [
          'Stylus (Apple Pencil / S-Pen) + Keyboard Folio',
          'Stylus Only',
          'Smart Folio Protective Case Only',
          'Tablet Only',
        ],
      },
      {
        key: 'connectivity',
        label: 'Connectivity',
        type: 'select',
        defaultValue: 'Wi-Fi Only',
        options: ['Wi-Fi Only', 'Wi-Fi + 5G Cellular'],
      },
    ],
  },
  headset: {
    id: 'headset',
    label: 'Headset / Audio / Webcam',
    defaultName: 'Headset',
    icon: Headphones,
    badge: 'Audio & Video Details',
    fields: [
      {
        key: 'deviceType',
        label: 'Device Type',
        type: 'select',
        defaultValue: 'Wireless ANC Over-Ear Headset',
        options: [
          'Wireless ANC Over-Ear Headset (Zoom/Teams Certified)',
          'USB-C Wired Call Headset with Boom Mic',
          'True Wireless Earbuds with ANC (AirPods Pro / Jabra)',
          '4K Ultra HD Conference Webcam',
          'USB Desktop Microphone (Podcasting / Studio)',
        ],
      },
      {
        key: 'connection',
        label: 'Connection Interface',
        type: 'select',
        defaultValue: 'USB Dongle + Bluetooth Multipoint',
        options: [
          'USB Dongle + Bluetooth Multipoint',
          'USB-C Wired',
          '3.5mm Audio Jack',
          'Bluetooth Only',
        ],
      },
      {
        key: 'micFeatures',
        label: 'Microphone & Features',
        type: 'select',
        defaultValue: 'AI Noise-Cancelling Boom Mic',
        options: [
          'AI Noise-Cancelling Boom Mic',
          'Dual Beamforming Mics with Mute LED',
          'Standard Integrated Mic',
        ],
      },
    ],
  },
  peripherals: {
    id: 'peripherals',
    label: 'Keyboards & Mice',
    defaultName: 'Keyboard & Mouse',
    icon: SlidersHorizontal,
    badge: 'Peripheral Details',
    fields: [
      {
        key: 'itemType',
        label: 'Item Type',
        type: 'select',
        defaultValue: 'Ergonomic Wireless Keyboard & Mouse Combo',
        options: [
          'Ergonomic Wireless Keyboard & Mouse Combo',
          'Mechanical Keyboard (Tactile / Quiet switches)',
          'Precision Ergonomic Mouse (e.g. Logitech MX Master 3S)',
          'Vertical Ergonomic Mouse',
          'Apple Magic Keyboard & Trackpad',
        ],
      },
      {
        key: 'connection',
        label: 'Connectivity',
        type: 'select',
        defaultValue: 'Bluetooth & 2.4GHz Wireless',
        options: ['Bluetooth & 2.4GHz Wireless', 'USB-C Wired', 'Bluetooth Only'],
      },
      {
        key: 'formFactor',
        label: 'Form Factor / Layout',
        type: 'select',
        defaultValue: 'Full-Size with Number Pad',
        options: ['Full-Size with Number Pad', 'Compact Tenkeyless (TKL / 75%)', 'Split Ergonomic Curved'],
      },
    ],
  },
  dock: {
    id: 'dock',
    label: 'Docking Station & Hubs',
    defaultName: 'Docking Station',
    icon: Cpu,
    badge: 'Docking Specifications',
    fields: [
      {
        key: 'standard',
        label: 'Dock Standard',
        type: 'select',
        defaultValue: 'Thunderbolt 4 / USB4 Dual 4K Dock',
        options: [
          'Thunderbolt 4 / USB4 Dual 4K Dock',
          'Universal USB-C Triple Display Dock',
          'Compact Multiport USB-C Travel Hub',
        ],
      },
      {
        key: 'powerDelivery',
        label: 'Power Delivery (PD)',
        type: 'select',
        defaultValue: '100W PD (Full Laptop Charging)',
        options: ['100W PD (Full Laptop Charging)', '85W - 90W PD', '65W PD', 'Bus-Powered (No Wall Adapter)'],
      },
      {
        key: 'videoOutputs',
        label: 'Display Outputs',
        type: 'select',
        defaultValue: 'Dual DisplayPort + Dual HDMI',
        options: ['Dual DisplayPort + Dual HDMI', 'Dual HDMI Only', 'DisplayPort + HDMI + USB-C Video'],
      },
    ],
  },
  server_network: {
    id: 'server_network',
    label: 'Server & Network Gear',
    defaultName: 'Server / Network',
    icon: Server,
    badge: 'Infrastructure Specs',
    fields: [
      {
        key: 'equipmentType',
        label: 'Equipment Type',
        type: 'select',
        defaultValue: 'Rackmount Server',
        options: [
          'Rackmount Server',
          'Managed Gigabit PoE+ Network Switch',
          'Enterprise Wi-Fi 6/7 Access Point',
          'Security Gateway / Next-Gen Firewall',
          'Network Attached Storage (NAS)',
        ],
      },
      {
        key: 'formFactor',
        label: 'Form Factor',
        type: 'select',
        defaultValue: '1U Rackmount',
        options: ['1U Rackmount', '2U Rackmount', 'Desktop / Compact Wall-Mount', '4U / Tower Chassis'],
      },
      {
        key: 'interfaces',
        label: 'Network Ports',
        type: 'select',
        defaultValue: '2x 10GbE SFP+ + 4x 1GbE RJ45',
        options: [
          '2x 10GbE SFP+ + 4x 1GbE RJ45',
          '24-Port Gigabit PoE+ (370W)',
          '48-Port Gigabit PoE+ (740W)',
          'Dual 25GbE SFP28 Ports',
        ],
      },
      {
        key: 'powerRedundancy',
        label: 'Power Supply Redundancy',
        type: 'select',
        defaultValue: 'Dual Hot-Plug Redundant PSUs',
        options: ['Dual Hot-Plug Redundant PSUs', 'Single Power Supply'],
      },
    ],
  },
  printer: {
    id: 'printer',
    label: 'Printers & Scanners',
    defaultName: 'Printer',
    icon: Printer,
    badge: 'Printing / Scanning Details',
    fields: [
      {
        key: 'printerType',
        label: 'Printer Type',
        type: 'select',
        defaultValue: 'Multifunction Color Laser (Print / Scan / Copy)',
        options: [
          'Multifunction Color Laser (Print / Scan / Copy)',
          'Multifunction Monochrome Laser',
          'High-Duty Workgroup Network Laser',
          'Desktop Barcode & Thermal Label Printer',
        ],
      },
      {
        key: 'duplexScanning',
        label: 'Duplex & Feeder',
        type: 'select',
        defaultValue: 'Automatic 2-Sided Duplex + 50-Sheet ADF',
        options: ['Automatic 2-Sided Duplex + 50-Sheet ADF', 'Standard 2-Sided Duplex', 'Single-Sided Manual'],
      },
      {
        key: 'networkConn',
        label: 'Connectivity',
        type: 'select',
        defaultValue: 'Ethernet RJ45 + Wi-Fi + AirPrint',
        options: ['Ethernet RJ45 + Wi-Fi + AirPrint', 'Ethernet RJ45 Only', 'USB Only'],
      },
    ],
  },
  other: {
    id: 'other',
    label: 'Other Hardware',
    defaultName: 'Hardware Equipment',
    icon: Sparkles,
    badge: 'Custom Specifications',
    fields: [
      {
        key: 'preferredBrand',
        label: 'Preferred Brand / Manufacturer',
        type: 'text',
        placeholder: 'e.g. Dell, Apple, Lenovo, Logitech, Sony',
        defaultValue: '',
      },
      {
        key: 'modelSku',
        label: 'Exact Model / SKU / Part #',
        type: 'text',
        placeholder: 'e.g. Precision 5680 / MX-923',
        defaultValue: '',
      },
      {
        key: 'customSpecs',
        label: 'Specific Technical Requirements',
        type: 'text',
        placeholder: 'e.g. Needs HDMI 2.1, 100W PD, or specific dimensions',
        defaultValue: '',
      },
    ],
  },
};

export const PRESET_CATEGORIES = [
  { id: 'laptop', label: 'Laptop', icon: Laptop },
  { id: 'monitor', label: 'Monitor', icon: Monitor },
  { id: 'desktop', label: 'Desktop', icon: HardDrive },
  { id: 'phone', label: 'Phone', icon: Smartphone },
  { id: 'tablet', label: 'Tablet', icon: Tablet },
  { id: 'headset', label: 'Audio / Headset', icon: Headphones },
  { id: 'peripherals', label: 'Keyboard & Mouse', icon: SlidersHorizontal },
  { id: 'dock', label: 'Docking Station', icon: Cpu },
  { id: 'server_network', label: 'Server / Network', icon: Server },
  { id: 'printer', label: 'Printer', icon: Printer },
  { id: 'other', label: 'Other', icon: Sparkles },
];

/**
 * Intelligent categorization based on user input string.
 */
export function detectAssetCategory(assetType: string): string {
  const t = (assetType || '').toLowerCase().trim();
  if (!t) return 'laptop';
  if (/monitor|display|screen|ultrasharp|curved|4k display/i.test(t)) return 'monitor';
  if (/laptop|macbook|thinkpad|notebook|zenbook|xps|latitude|chromebook/i.test(t)) return 'laptop';
  if (/desktop|workstation|mac mini|mac studio|optiplex|tower|all-in-one|imac/i.test(t)) return 'desktop';
  if (/phone|iphone|galaxy|pixel|android|smartphone|cellular|mobile/i.test(t)) return 'phone';
  if (/tablet|ipad|galaxy tab|surface pro/i.test(t)) return 'tablet';
  if (/keyboard|mouse|trackpad|peripheral|ergonomic/i.test(t)) return 'peripherals';
  if (/headset|headphone|earphone|airpod|webcam|camera|mic|microphone|audio/i.test(t)) return 'headset';
  if (/dock|docking|hub|adapter|thunderbolt/i.test(t)) return 'dock';
  if (/server|switch|router|access point|firewall|rack|nas|networking/i.test(t)) return 'server_network';
  if (/printer|scanner|copier|label/i.test(t)) return 'printer';
  return 'other';
}

/**
 * Default specifications map for a category.
 */
export function getDefaultSpecs(categoryId: string): Record<string, string> {
  const config = ASSET_CATEGORY_CONFIGS[categoryId] || ASSET_CATEGORY_CONFIGS.other;
  const out: Record<string, string> = {};
  for (const f of config.fields) {
    if (f.defaultValue) {
      out[f.key] = f.defaultValue;
    }
  }
  return out;
}

/**
 * Format specifications into clean markdown bullets.
 */
export function formatSpecsSummary(specs?: Record<string, any>): string {
  if (!specs || typeof specs !== 'object') return '';
  const entries = Object.entries(specs).filter(([_, v]) => v !== undefined && v !== null && String(v).trim() !== '');
  if (entries.length === 0) return '';
  return entries
    .map(([k, v]) => `• ${k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}: ${v}`)
    .join('\n');
}
